import { initializeApp } from 'firebase/app';
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult,
} from 'firebase/auth';
import { getAnalytics, setUserProperties } from 'firebase/analytics';
import { getMessaging, isSupported } from 'firebase/messaging';
import { notify } from './toastify';

// Extend Window interface to include confirmationResult
declare global {
  interface Window {
    confirmationResult?: ConfirmationResult;
  }
}

// const firebaseConfig = {
//   apiKey: 'AIzaSyCq0KQqNoCag4ufYPZduIE4vEiwAVi8tjg',
//   authDomain: 'reeltrak-staging.firebaseapp.com',
//   projectId: 'reeltrak-staging',
//   storageBucket: 'reeltrak-staging.firebasestorage.app',
//   messagingSenderId: '363742725289',
//   appId: '1:363742725289:web:d7c6c0d34146e1aed295b8',
// }

const firebaseConfig = {
  apiKey: process.env.FIREBASE_API_KEY,
  authDomain: process.env.FIREBASE_DOMAIN,
  projectId: process.env.FIREBASE_PROJECT_ID,
  storageBucket: process.env.FIREBASE_BUCKET,
  messagingSenderId: process.env.FIREBASE_SENDER_ID,
  appId: process.env.FIREBASE_APP_ID,
};

console.log('firebaseConfig =>>>>>', firebaseConfig);

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const analytics = getAnalytics(app);
setUserProperties(analytics, { platform: 'web' });
auth.useDeviceLanguage();

// Initialize messaging with proper error handling
let messaging: any = null;

const initializeMessaging = async () => {
  try {
    // Check if browser supports Firebase messaging
    const isMessagingSupported = await isSupported();

    if (isMessagingSupported) {
      messaging = getMessaging(app);
      console.log('✅ Firebase messaging initialized successfully');
    } else {
      console.warn('⚠️ Firebase messaging is not supported in this browser');
      messaging = null;
    }
  } catch (error) {
    console.warn('⚠️ Failed to initialize Firebase messaging:', error);
    messaging = null;
  }
};

// Initialize messaging on module load
initializeMessaging();

// Export messaging with getter function for better error handling
export const getMessagingInstance = () => {
  if (!messaging) {
    console.warn('⚠️ Firebase messaging not initialized');
  }
  return messaging;
};

export { messaging };

let recaptchaVerifier: RecaptchaVerifier | null = null;

// export const setupRecaptcha = async () => {
//   // ✅ Destroy previous reCAPTCHA instance if exists
//   if (window.recaptchaVerifier) {
//     console.log('=>>> in we are in window recaptcha')
//     try {
//       window.recaptchaVerifier.clear()
//     } catch (err) {
//       console.log('Error clearing reCAPTCHA:', err)
//     }
//     window.recaptchaVerifier = null
//   }

//   console.log('Initializing new reCAPTCHA...')
//   window.recaptchaVerifier = new RecaptchaVerifier(
//     auth,
//     'recaptcha-container',
//     {
//       size: 'invisible', // Use 'normal' if needed
//       callback: (response) => {
//         console.log('reCAPTCHA solved:', response)
//       },
//       'expired-callback': () => {
//         console.log('reCAPTCHA expired. Resetting...')
//         window.recaptchaVerifier = null // Reset if expired
//       },
//     }
//   )
//   window.recaptchaVerifier.render() // ✅ Ensure it's rendered once
// }

export const sendOTP = async (phoneNumber: any) => {
  try {
    // setupRecaptcha() // ✅ Ensure Recaptcha is ready
    // const appVerifier = window.recaptchaVerifier

    // if (!appVerifier) {
    //   console.error('Error: reCAPTCHA is not initialized')
    //   return
    // }

    if (!recaptchaVerifier) {
      recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible', // Use 'normal' if needed
        callback: (response: string) => {
          console.log('reCAPTCHA solved:', response);
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired. Resetting...');
          // window.recaptchaVerifier = null // Reset if expired
        },
      });
    }

    const confirmationResult = await signInWithPhoneNumber(
      auth,
      phoneNumber,
      recaptchaVerifier,
    );
    window.confirmationResult = confirmationResult;
    return confirmationResult;
  } catch (error) {
    console.error('Error sending OTP:', error);
    throw error;
  }
};

export const verifyOtp = async (otp: any, setOtpStatus?: any) => {
  try {
    const confirmationResult = window.confirmationResult;
    // const credential = PhoneAuthProvider.credential(
    //   confirmationResult.verificationId,
    //   otp
    // )
    // const userCredential = await signInWithCredential(auth, credential)
    const userCredential = await confirmationResult?.confirm(otp);
    console.log('User signed in:', userCredential);
    return userCredential;
  } catch (error) {
    console.error('Error verifying OTP:', error);
    handleConfirmOtpError(error);
    setOtpStatus(false);
    // notify('Invalid OTP. Please try again.', 'error')
  }
};

const handleConfirmOtpError = (error: any) => {
  console.log('Confirm OTP Error:', error);
  switch (error.code) {
    case 'auth/invalid-verification-code':
      notify('The OTP code entered is invalid. Please try again.', 'error');
      break;
    case 'auth/session-expired':
      notify('The OTP session has expired. Please resend the OTP.', 'error');
      break;
    case 'auth/code-expired':
      notify(
        'The verification code has expired. Please request a new OTP.',
        'error',
      );
      break;
    case 'auth/invalid-verification-id':
      notify(
        'Verification failed due to an invalid verification ID. Please retry.',
        'error',
      );
      break;
    case 'auth/network-request-failed':
      notify('Network error. Please check your internet connection.', 'error');
      break;
    default:
      notify(
        `An error occurred during verification. Please try again.`,
        'error', //remove error code before uploading the builds on app store/play console
      );
      break;
  }
};

export let firebaseMessagingToken: any = '';
let tokenRefreshCallbacks: ((token: string) => void)[] = [];

// Add callback for token refresh events
export const onTokenRefreshCallback = (callback: (token: string) => void) => {
  tokenRefreshCallbacks.push(callback);

  // Return unsubscribe function
  return () => {
    tokenRefreshCallbacks = tokenRefreshCallbacks.filter(cb => cb !== callback);
  };
};

// Get messaging token with proper error handling
export const getMessagingToken = async (vapidKey?: string) => {
  try {
    if (!messaging) {
      console.warn('⚠️ Firebase messaging is not available');
      return null;
    }

    const token = await getToken(
      messaging,
      vapidKey ? { vapidKey } : undefined,
    );
    firebaseMessagingToken = token;
    console.log(
      '✅ Firebase messaging token obtained:',
      token ? 'Success' : 'No token',
    );
    return token;
  } catch (error) {
    console.warn('⚠️ Failed to get Firebase messaging token:', error);
    firebaseMessagingToken = '';
    return null;
  }
};

// Initialize token when messaging is ready
const initializeToken = async () => {
  if (messaging) {
    await getMessagingToken();
  } else {
    // Retry after a short delay in case messaging initializes later
    setTimeout(async () => {
      if (messaging) {
        await getMessagingToken();
      }
    }, 1000);
  }
};

// Initialize token
initializeToken();
